import React, { Fragment, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import excelFormatSample from "../../assets/files/exceluploadformate_sample.csv"; // Sample file with example data
import excelFormatEmpty from "../../assets/files/exceluploadformate_empty.csv"; // Empty template file
import {
  Card,
  CardBody,
  Button,
  Container,
  Form,
  FormGroup,
  Label,
  CardHeader,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import { showToast } from "../../Components/Common/Toast";
import {
  bulkUploadRequest,
  bulkUploadReset,
} from "../../redux/recruiter/candidate_bulk_upload/actions";
import {
  candidateBulkExcelUploadRequest,
  candidateBulkExcelUploadReset,
} from "../../redux/recruiter/candidate_bulk_excel_upload/actions";
import Select from "react-select";
import { fetchJobNamesRequest } from "../../redux/recruiter/job_name/actions";
import { FiUpload } from "react-icons/fi";
import { AuthContext } from "../../Auth/AuthContext";

/** Matches API `outcome` from add_bulk_resume_files / bulk_upload_csv */
const BULK_UPLOAD_OUTCOME_TOAST = {
  SAME_COMPANY_EXISTING_NO_JOB: {
    type: "warning",
    message: "Candidate already exists.",
  },
  SAME_COMPANY_EXISTING_JOB_ADDED: {
    type: "warning",
    message:
      "Candidate already exists. The candidate has been added to the job.",
  },
  SAME_COMPANY_EXISTING_JOB_ALREADY: {
    type: "warning",
    message: "Candidate already exists. Already applied to this job.",
  },
  NEW_COMPANY_SAVE_ONLY: {
    type: "success",
    message: "Candidates added successfully. Candidate saved successfully.",
  },
  NEW_COMPANY_JOB_ADDED: {
    type: "success",
    message: "Candidates added successfully. Candidate added to the job.",
  },
  NEW_COMPANY_JOB_ALREADY: {
    type: "success",
    message:
      "Candidates added successfully. Candidate saved successfully. Already applied to this job.",
  },
  EXISTING_GLOBAL_NO_TRACK: {
    type: "warning",
    message: "Candidate already exists.",
  },
};

const BulkUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerTotalPoints } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    jobId: "", // Set to null initially
    jobName: "",
  });
  const [jobNameOptions, setJobNameOptions] = useState([]);
  const jobNameReducerData = useSelector((state) => state.jobNameReducer);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("RESUME_UPLOAD");
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const candidateBulkExcelUploadReducerData = useSelector(
    (state) => state.candidateBulkExcelUploadReducer
  );

  const candidateBulkUploadReducerData = useSelector(
    (state) => state.candidateBulkUploadReducer
  );
  const customOptionsStyle = {
    control: (base) => ({
      ...base,
      minHeight: 35,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  useEffect(() => {
    if (jobNameReducerData.data?.data?.length > 0) {
      const options = jobNameReducerData.data.data.map((job) => ({
        value: job.value,
        label: job.label,
      }));
      setJobNameOptions(options);
    } else {
      setJobNameOptions([]);
    }
  }, [jobNameReducerData.data]);

  const handleJobNameFocus = () => {
    dispatch(fetchJobNamesRequest(""));
  };

  const handleJobNameInputChange = (inputValue) => {
    if (inputValue) {
      dispatch(fetchJobNamesRequest(inputValue));
    }
  };

  const handleJobNameChange = (selectedOption) => {
    setFormData({
      jobId: selectedOption ? selectedOption.value : "",
      jobName: selectedOption ? selectedOption.label : "",
    });
  };

  const getUploadParams = () => ({ url: "/" });

  const handleChangeStatus = ({ meta, file, remove }, status) => {
    const maxFileSize = 5 * 1024 * 1024; // 5MB limit

    if (status === "ready" || status === "done") {
      if (file && file.size > maxFileSize) {
        setErrors((prev) => ({
          ...prev,
          uploadedFiles: `File "${file.name}" exceeds 5MB limit.`,
        }));
        showToast("error", `File "${file.name}" exceeds 5MB limit.`);
        if (remove) remove();
        return;
      }

      setErrors((prev) => ({ ...prev, uploadedFiles: "" }));
      setUploadedFiles((prevFiles) => {
        const exists = prevFiles.some(
          (f) => f.meta?.id === meta?.id || f.file?.name === file.name
        );
        if (exists) {
          return prevFiles.map((f) =>
            f.meta?.id === meta?.id || f.file?.name === file.name
              ? { file, meta }
              : f
          );
        }
        return [...prevFiles, { file, meta }];
      });
    } else if (
      status === "removed" ||
      status === "aborted" ||
      status === "cancelled"
    ) {
      setUploadedFiles((prevFiles) =>
        prevFiles.filter(
          (f) => f.meta?.id !== meta?.id && f.file?.name !== file.name
        )
      );
    }
  };

  const validate = () => {
    const errors = {};
    let isValid = true;
    if (uploadedFiles.length === 0) {
      errors.uploadedFiles = "Please upload at least one file";
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const tabOptions = [
    {
      value: "RESUME_UPLOAD",
      label: "RESUME UPLOAD",
    },
    {
      value: "EXCEL_UPLOAD",
      label: "EXCEL UPLOAD (CSV)",
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      if (uploadedFiles.length > 0) {
        const submissionData = new FormData();
        console.log("uploadedFiles", uploadedFiles);
        if (activeTab === "RESUME_UPLOAD") {
          uploadedFiles.forEach(({ file }, index) => {
            submissionData.append(`jobseeker_resume_file${index}`, file);
          });
          submissionData.append("job_id", formData.jobId || ""); // Correctly appending job_id from state
          console.log("submissionData", submissionData);
          dispatch(bulkUploadRequest(submissionData));
        } else if (activeTab === "EXCEL_UPLOAD") {
          uploadedFiles.forEach(({ file }, index) => {
            submissionData.append("jobseeker_resume_file", file);
          });
          submissionData.append("job_id", formData.jobId || ""); // Correctly appending job_id from state
          dispatch(candidateBulkExcelUploadRequest(submissionData));
        }
        // navigate(${process.env.PUBLIC_URL}/jobseeker/resume_scan_result);
      } else {
        showToast("error", "Please upload at least one file before scanning");
      }
    }
  };

  useEffect(() => {
    if (
      candidateBulkUploadReducerData.status == true &&
      candidateBulkUploadReducerData.message != ""
    ) {
      const outcomes = candidateBulkUploadReducerData.upload_outcomes || [];
      if (outcomes.length > 0) {
        outcomes.forEach((row) => {
          const cfg = row?.outcome
            ? BULK_UPLOAD_OUTCOME_TOAST[row.outcome]
            : null;
          if (cfg) {
            showToast(cfg.type, cfg.message);
          } else if (row?.file_name) {
            showToast(
              "success",
              candidateBulkUploadReducerData.message || "Saved successfully"
            );
          }
        });
      } else {
        showToast(
          "success",
          candidateBulkUploadReducerData.message || "Saved successfully"
        );
      }
      triggerTotalPoints(
        "bulk_upload",
        false,
        candidateBulkUploadReducerData.parsed_resumes
      );
      setFormData({ jobId: "", jobName: "" });
      setUploadedFiles([]);
      setErrors({});
      setDropzoneKey((prev) => prev + 1);
      dispatch(bulkUploadReset());
    } else if (
      (candidateBulkUploadReducerData.status == false ||
        candidateBulkUploadReducerData.status === undefined) &&
      candidateBulkUploadReducerData.message
    ) {
      showToast("error", candidateBulkUploadReducerData.message);
      dispatch(bulkUploadReset());
    }
  }, [candidateBulkUploadReducerData]);

  useEffect(() => {
    if (
      candidateBulkExcelUploadReducerData.status == true &&
      candidateBulkExcelUploadReducerData.message != ""
    ) {
      const rows = candidateBulkExcelUploadReducerData.results || [];
      if (rows.length > 0) {
        rows.forEach((row) => {
          const cfg = row?.outcome
            ? BULK_UPLOAD_OUTCOME_TOAST[row.outcome]
            : null;
          if (cfg) {
            showToast(cfg.type, cfg.message);
            return;
          }
          showToast(
            row?.status === false ? "warning" : "success",
            row?.message || "Processed successfully"
          );
        });
      } else {
        showToast(
          "success",
          candidateBulkExcelUploadReducerData.message || "Processed successfully"
        );
      }
      triggerTotalPoints(
        "bulk_upload",
        false,
        candidateBulkExcelUploadReducerData.parsed_resumes
      );
      setFormData({ jobId: "", jobName: "" });
      setUploadedFiles([]);
      setErrors({});
      setDropzoneKey((prev) => prev + 1);
      dispatch(candidateBulkExcelUploadReset());
    } else if (
      (candidateBulkExcelUploadReducerData.status == false ||
        candidateBulkExcelUploadReducerData.status === undefined) &&
      candidateBulkExcelUploadReducerData.message
    ) {
      showToast("error", candidateBulkExcelUploadReducerData.message);
      dispatch(candidateBulkExcelUploadReset());
    }
  }, [candidateBulkExcelUploadReducerData]);

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Bulk Upload" title="Bulk Upload" />
      <Container fluid>
        <Card className="bg-white">
          <CardHeader className="py-3">
            <Nav
              className="nav-pills nav-primary justify-content-start mb-3"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              {tabOptions.map((item, i) => (
                <NavItem key={i}>
                  <NavLink
                    className={`font-weight-bold ${
                      activeTab === item.value ? "active" : ""
                    }`}
                    onClick={() => {
                      setActiveTab(item.value);
                      setUploadedFiles([]);
                      setErrors({});
                      setDropzoneKey((prev) => prev + 1);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {item.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </CardHeader>
          <CardBody>
            <Form className="theme-form">
              <FormGroup style={{ position: "relative", zIndex: 1000 }}>
                <Label>Select Job</Label>
                <Select
                  isLoading={jobNameReducerData.isLoading}
                  options={jobNameOptions}
                  value={
                    jobNameOptions.find(
                      (opt) => opt.value === formData.jobId
                    ) || null
                  }
                  onChange={handleJobNameChange}
                  styles={customOptionsStyle}
                  onFocus={handleJobNameFocus}
                  onInputChange={handleJobNameInputChange}
                  placeholder="Select job name..."
                  isClearable={true}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </FormGroup>
              {activeTab === "RESUME_UPLOAD" && (
                <FormGroup>
                  <Label>
                    Resume Upload
                    <span className="text-danger">*</span>
                  </Label>
                  <div className="dropzone-wrapper">
                    <Dropzone
                      key={`resume-dropzone-${dropzoneKey}`}
                      getUploadParams={getUploadParams}
                      onChangeStatus={handleChangeStatus}
                      maxFiles={10}
                      multiple={true}
                      canCancel={true}
                      autoUpload={false}
                      accept=".pdf,.docx,.doc"
                      inputContent={
                        <div style={{ textAlign: "center", cursor: "pointer" }}>
                          <FiUpload size={40} />
                          <h5 className="mt-2">
                            Drag and drop files to upload candidates
                            (.pdf,.docx,.doc)
                          </h5>
                          <p className="text-muted mb-0 mt-1" style={{ fontSize: "13px" }}>
                            Max 10 files &nbsp;|&nbsp; Supported Formats: <strong>.pdf, .doc, .docx</strong> (Max: 5MB)
                          </p>
                        </div>
                      }
                      styles={{
                        dropzone: {
                          minHeight: 180,
                          borderColor: "#007bff",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "5px",
                          display: "inline-flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "10px",

                        },
                        preview: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "calc(33.33% - 10px)", // Adjust width to fit three items per row
                          boxSizing: "border-box",
                          margin: "10px 0",
                          borderBottom: "0px"
                        },
                        previewImage: {
                          display: "none",
                        },
                        previewName: {
                          display: "block",
                          padding: "10px",
                          textAlign: "center",
                        },
                        inputLabelWithFiles: {
                          // Align content with files uploaded
                          marginTop: "4rem",
                          justifyContent: "center",
                        },
                      }}
                    />
                  </div>
                  {errors.uploadedFiles && (
                    <small className="text-danger">
                      {errors.uploadedFiles}
                    </small>
                  )}
                </FormGroup>
              )}
              {activeTab === "EXCEL_UPLOAD" && (
                <FormGroup>
                  <Label>
                    Excel Upload
                    <span className="text-danger">*</span>
                  </Label>
                  <div className="dropzone-wrapper">
                    <Dropzone
                      key={`excel-dropzone-${dropzoneKey}`}
                      getUploadParams={getUploadParams}
                      onChangeStatus={handleChangeStatus}
                      maxFiles={1}
                      multiple={false}
                      canCancel={true}
                      autoUpload={false}
                      accept=".csv"
                      inputContent={
                        <div style={{ textAlign: "center", cursor: "pointer" }}>
                          <FiUpload size={40} />
                          <h5 className="mt-2">
                            Drag and drop files to upload candidates (.csv)
                          </h5>
                          <p className="text-muted mb-0 mt-1" style={{ fontSize: "13px" }}>
                            Single CSV file &nbsp;|&nbsp; Supported Format: <strong>.csv</strong> (Max: 5MB)
                          </p>
                        </div>
                      }
                      styles={{
                        dropzone: {
                          minHeight: 180,
                          borderColor: "#007bff",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "5px",
                          display: "inline-flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "10px",
                        },
                        preview: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "calc(33.33% - 10px)", // Adjust width to fit three items per row
                          boxSizing: "border-box",
                          margin: "10px 0",
                          borderBottom: "0px"
                        },
                        previewImage: {
                          display: "none",
                        },
                        previewName: {
                          display: "block",
                          padding: "10px",
                          textAlign: "center",
                        },
                        inputLabelWithFiles: {
                          // Align content with files uploaded
                          marginTop: "4rem",
                          justifyContent: "center",
                        },
                      }}
                    />
                  </div>
                  {errors.uploadedFiles && (
                    <small className="text-danger">
                      {errors.uploadedFiles}
                    </small>
                  )}{" "}
                  <br />
                  <div 
                    className="d-flex justify-content-center p-3"
                    style={{
                      backgroundColor: "#f8f9ff",
                      border: "2px solid #007bff",
                      borderRadius: "8px",
                      marginTop: "15px"
                    }}
                  >
                    <div className="text-center">
                      <p style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#333" }}>
                        <strong>📋 CSV Upload Instructions:</strong>
                      </p>
                      <p style={{ fontSize: "15px", marginBottom: "15px", color: "#555", lineHeight: "1.6" }}>
                        Upload your CSV file with a header row. Make sure the CSV file has columns for all the
                        required fields.
                      </p>
                      <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                        <a
                          href={excelFormatSample}
                          download="candidates_sample.csv"
                          style={{
                            display: "inline-block",
                            padding: "10px 16px",
                            backgroundColor: "#007bff",
                            color: "white",
                            borderRadius: "5px",
                            textDecoration: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#0056b3";
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow = "0 4px 8px rgba(0,91,179,0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#007bff";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          📥 Download Sample CSV
                        </a>
                        <a
                          href={excelFormatEmpty}
                          download="candidates_template.csv"
                          style={{
                            display: "inline-block",
                            padding: "10px 16px",
                            backgroundColor: "#28a745",
                            color: "white",
                            borderRadius: "5px",
                            textDecoration: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#1e7e34";
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow = "0 4px 8px rgba(30,126,52,0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#28a745";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          📋 Download Empty Template
                        </a>
                      </div>
                      <p style={{ fontSize: "13px", marginTop: "12px", color: "#666", fontStyle: "italic" }}>
                        Follow the sample format and add your candidate data in the empty template.
                      </p>
                    </div>
                  </div>
                </FormGroup>
              )}
              <Button
                color="primary"
                type="submit"
                onClick={handleSubmit}
                disabled={
                  candidateBulkExcelUploadReducerData.isLoading ||
                  candidateBulkUploadReducerData.isLoading ||
                  uploadedFiles.length === 0
                }
              >
                {candidateBulkExcelUploadReducerData.isLoading ||
                candidateBulkUploadReducerData.isLoading ? (
                  <Fragment>
                    <Spinner size="sm" color="light" style={{ marginRight: "8px" }} />
                    Uploading...
                  </Fragment>
                ) : (
                  "Submit"
                )}
              </Button>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default BulkUpload;
